import React, { useState } from "react";
import Header from "../components/header";
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import ContactForm from "../components/ContactForm";
import CallbackPopup from "../components/CallbackPopup";
import { RiMapPinFill } from "react-icons/ri";
import TopHospitalsThailand from "../components/TopHospitalsThailand";
import BestDoctorsThailand from "../components/BestDoctorsThailand";
import ThailandBlogsCarousel from "../components/ThailandBlogsCarousel";
import { useNavigate } from 'react-router-dom';

const ThailandMedicalPage = () => {
  const navigate = useNavigate();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  return (
    <div className="bg-white">
      <Header />

      {/* Banner */}
      <section className="w-full">
        <img
          src="/images/Thai1.webp"
          alt="Thailand Banner"
          className="w-full h-auto"
        />
      </section>

      {/* Culture Section */}
      <section className="py-20 mt-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="md:w-1/2">
              <img
                src="/images/thai2.webp"
                alt="Thai Culture"
                className="w-full h-auto rounded"
              />
            </div>
            <div className="md:w-1/2 md:pl-12 ml-6">
              <h4 className="text-3xl font-bold mb-6 text-gray-800">
                Information on Thai Culture
              </h4>
              <p className="text-[18px] text-gray-700 leading-relaxed mb-6">
                Thailand offers a serene healing environment, world-class
                medical facilities, and warm Thai hospitality. From ancient
                therapies to modern treatments, Thailand is one of the top
                destinations for medical tourism.
              </p>
              <ul className="grid grid-cols-2 gap-y-4 gap-x-10 list-disc pl-6 text-[18px] text-gray-800 leading-relaxed marker:text-blue-600 marker:text-2xl">
                <li>Affordable Quality Care</li>
                <li>Peaceful Healing Environment</li>
                <li>Traditional Thai Therapies</li>
                <li>Renowned Thai Hospitality</li>
              </ul>
              <div className="flex gap-4 mt-10">
                <a
                  href="https://wa.me/9599222655"
                  className="bg-blue-200 text-blue-800 px-4 py-2 rounded"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn More
                </a>
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                  onClick={() => setIsCallbackOpen(true)}
                >
                  Get a call back
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cities in Thailand */}
      <section className="py-10 mt-10 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-5xl font-bold">
            <span className="text-blue-600">Thai</span> Cities
          </h3>
          <p className="mt-3 text-2xl">
            These cities in Thailand are known for exceptional medical and
            wellness care.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
            {[
              "Bangkok",
              "Chiang Mai",
              "Phuket",
              "Pattaya",
              "Krabi",
              "Hua Hin",
              "Ko Samui",
              "Ayutthaya",
            ].map((city) => (
              <div
                key={city}
                className="bg-white border p-6 shadow text-center rounded-2xl flex flex-col justify-center items-center h-60"
              >
                <RiMapPinFill className="text-blue-600 text-3xl mb-2" />
                <h5 className="font-semibold text-lg">{city}</h5>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments Section */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col-reverse md:flex-row gap-8 items-center">
            <div className="md:w-1/2">
              <h2 className="text-4xl font-bold mb-8">
                Thailand excels in the following medical treatments
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 mt-10">
                {[
                  "Wellness Retreats",
                  "Plastic Surgery",
                  "Fertility Services",
                  "Eye Treatments",
                  "Dental Care",
                  "Orthopaedics",
                ].map((treatment) => (
                  <div key={treatment} className="flex flex-col">
                    <div className="flex items-center mb-2">
                      <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {treatment}
                      </h3>
                    </div>
                    <a
                      href="/speciality/cardiology"
                      className="text-blue-600 text-sm hover:text-blue-800 hover:underline transition-colors ml-6"
                    >
                      Learn More
                    </a>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:w-1/2">
              <img
                src="/images/thai3.webp"
                alt="Thailand Treatments"
                className="w-full h-auto rounded"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-10 bg-gray-50" id="contact-form">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold">
            Searching for a treatment in Thailand?
          </h2>
          <p className="mb-6">
            Fill the form to get a call back from our support team
          </p>
          <div className="mx-auto">
            <ContactForm formType="thailand-medical" />
          </div>
        </div>
      </section>
      <TopHospitalsThailand />
      <BestDoctorsThailand />

      {/* Tourism & Leisure */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-lg font-semibold">Tourism & Leisure</p>
            <h3 className="text-3xl md:text-4xl font-bold">
              <span className="text-[#1976f2]">Things to do</span> in Thailand
            </h3>
            <p className="mt-3 text-gray-600">
              Thailand is known for its beaches, temples, and traditional
              wellness. <br />
              It offers a perfect balance of recovery and recreation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Beaches",
                desc: "Thailand’s pristine beaches like Patong and Railay are perfect for post-treatment relaxation.",
              },
              {
                title: "Temples",
                desc: "Visit peaceful Buddhist temples for spiritual solace and healing experiences.",
              },
              {
                title: "Traditional Spa",
                desc: "Indulge in ancient Thai massages and herbal therapies for full-body rejuvenation.",
              },
              {
                title: "Cuisine",
                desc: "Thai cuisine is healthy, flavorful, and healing — with herbs and spices that aid recovery.",
              },
              {
                title: "Markets",
                desc: "Explore floating markets and night bazaars for a delightful cultural shopping experience.",
              },
              {
                title: "Festivals",
                desc: "Celebrate Songkran or Loy Krathong to immerse in Thai culture and festivities.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white border rounded-lg shadow-md p-6 flex flex-col justify-between"
              >
                <div>
                  <h5 className="text-xl font-semibold mb-2">{item.title}</h5>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
                <a
                  href="/thailand-tourism"
                  className="mt-4 text-[#1976f2] font-medium inline-flex items-center hover:underline"
                >
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thailand Blogs Carousel */}
      <ThailandBlogsCarousel title="Latest Medical Tourism Insights for Thailand" limit={6} />

      <section className="py-12">
        <div className="w-full flex justify-center px-4">
          <div className="w-full max-w-6xl bg-[#1976f2] rounded-xl shadow-lg p-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="md:w-2/3 mb-6 md:mb-0">
                <h4 className="text-white text-2xl md:text-3xl font-bold leading-snug">
                  Plan a relaxing treatment & <br /> stay tour in Thailand today
                </h4>
              </div>

              <div className="md:w-1/3 text-center">
                <p className="text-white font-medium mb-3">
                  We charge <br /> 0% planning fee
                </p>
                <a
                  href="https://wa.me/9599222655"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-white text-[#1976f2] font-semibold px-6 py-2 rounded-md hover:bg-gray-100 transition"
                >
                  <i className="ri-whatsapp-line mr-2"></i> Chat Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Callback Popup */}
      <CallbackPopup 
        isOpen={isCallbackOpen}
        onClose={() => setIsCallbackOpen(false)}
      />      <FloatingChatButton />

    </div>
  );
};

export default ThailandMedicalPage;
